// V2 toast store — V1 parity for showBanner (public/app/index.html L5467–5477).
// V1 `showBanner(msg, type)` — single DOM element, auto-dismiss 3.5s,
// types 'ok' | 'err' | ''. We expand to a stack with an 'info' variant.
//
// Auto-dismiss is handled here inside push() via setTimeout so callers can
// fire-and-forget. Manual dismiss cancels the timer.

import { create } from 'zustand'

export type ToastType = 'ok' | 'err' | 'info'

export interface Toast {
  id: number
  msg: string
  type: ToastType
  ttl: number
}

interface ToastState {
  toasts: Toast[]
  push: (msg: string, type?: ToastType, ttl?: number) => number
  dismiss: (id: number) => void
}

const DEFAULT_TTL = 3500 // V1 parity
const timers = new Map<number, ReturnType<typeof setTimeout>>()
let _id = 0

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],
  push: (msg, type = 'info', ttl = DEFAULT_TTL) => {
    const id = ++_id
    const toast: Toast = { id, msg, type, ttl }
    set((s) => ({ toasts: [...s.toasts, toast] }))
    if (ttl > 0) {
      const t = setTimeout(() => {
        get().dismiss(id)
      }, ttl)
      timers.set(id, t)
    }
    return id
  },
  dismiss: (id) => {
    const t = timers.get(id)
    if (t) {
      clearTimeout(t)
      timers.delete(id)
    }
    set((s) => ({ toasts: s.toasts.filter((x) => x.id !== id) }))
  },
}))

// Imperative helpers — usable outside React (e.g. mutation onError).
export function toastOk(msg: string, ttl?: number): number {
  return useToastStore.getState().push(msg, 'ok', ttl)
}
export function toastErr(msg: string, ttl?: number): number {
  return useToastStore.getState().push(msg, 'err', ttl)
}
export function toastInfo(msg: string, ttl?: number): number {
  return useToastStore.getState().push(msg, 'info', ttl)
}
